import { create } from 'zustand';
import { samGovApi, type ContractResponse } from '../lib/api/samGov';
import { toast } from 'react-hot-toast';

interface ContractFilters {
  status?: string;
  agency?: string;
  naicsCode?: string;
  setAside?: string;
}

interface ContractState {
  contracts: ContractResponse[];
  filters: ContractFilters;
  loading: boolean;
  error: string | null;
  fetchContracts: (filters?: ContractFilters) => Promise<void>;
  setFilters: (filters: ContractFilters) => void;
  exportContracts: (format: 'csv' | 'pdf', dateRange?: string) => Promise<void>;
}

export const useContractStore = create<ContractState>((set, get) => ({
  contracts: [],
  filters: {},
  loading: false,
  error: null,

  fetchContracts: async (filters = {}) => {
    set({ loading: true, error: null });
    try {
      // Check if we're using mock data
      if (import.meta.env.VITE_USE_MOCK_DATA === 'true') {
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Return mock data
        set({
          contracts: [
            {
              id: '1',
              title: 'Sample Contract 1',
              agency: 'Department of Defense',
              description: 'Sample contract description',
              value: 1000000,
              status: 'active',
              naicsCode: '541330',
              setAside: '8(a)',
              postedDate: new Date().toISOString(),
              deadlineDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              location: 'Washington, DC',
              noticeId: 'SAMPLE001',
              contractUrl: 'https://sam.gov',
              isUrlValid: true,
            },
            // Add more mock contracts as needed
          ],
          loading: false,
        });
        return;
      }

      // Real API call
      const apiKey = import.meta.env.VITE_SAM_GOV_API_KEY;
      if (!apiKey) {
        throw new Error('SAM.gov API key not configured');
      }

      const contracts = await samGovApi.fetchContracts({
        ...get().filters,
        ...filters,
      });

      set({ contracts, loading: false });
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch contracts';
      set({ error: errorMessage, loading: false });
      toast.error(errorMessage);
      
      // Fallback to empty array on error
      set({ contracts: [] });
    }
  },

  setFilters: (filters) => {
    set({ filters });
    get().fetchContracts(filters).catch(error => {
      console.error('Error applying filters:', error);
      toast.error('Failed to apply filters');
    });
  },

  exportContracts: async (format: 'csv' | 'pdf', dateRange?: string) => {
    try {
      const contracts = get().contracts;
      
      if (contracts.length === 0) {
        throw new Error('No contracts available to export');
      }

      // Filter contracts by date range if specified
      const filteredContracts = dateRange 
        ? contracts.filter(contract => {
            const contractDate = new Date(contract.postedDate);
            const today = new Date();
            const daysAgo = new Date(today.setDate(today.getDate() - parseInt(dateRange)));
            return contractDate >= daysAgo;
          })
        : contracts;

      await samGovApi.exportContracts(filteredContracts, format);
      toast.success(`Contracts exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to export contracts';
      toast.error(errorMessage);
      throw error;
    }
  },
}));